import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class AudioService {

  constructor() { }

  private convertFnUrl = 'https://convertwebmtooggandupload-xnmyigitzq-uc.a.run.app';
  // private convertFnUrl = 'http://127.0.0.1:5001/zapteck-2707e/us-central1/convertWebmToOggAndUpload';

  public async uploadWebmAndConvertToOgg(webmBlob: Blob, meta: { companyId: string; phone: string; messageType?: string }): Promise<{ url: string; contentType: string; filePath: string }> {

    const url =
      `${this.convertFnUrl}?companyId=${encodeURIComponent(meta.companyId)}`
      + `&phone=${encodeURIComponent(meta.phone)}`
      + `&messageType=${encodeURIComponent(meta.messageType ?? 'audio')}`;

    const resp = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/octet-stream'
      },
      body: webmBlob
    });

    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      throw new Error(`CONVERT_FN_FAILED_${resp.status}: ${text}`);
    }

    return await resp.json();
  }

  private convertFnM4aUrl = 'https://convertwebmtom4aandupload-xnmyigitzq-uc.a.run.app';

  public async uploadWebmAndConvertToM4a(webmBlob: Blob, meta: { companyId: string; phone: string; messageType?: string }): Promise<{ url: string; contentType: string; filePath: string }> {

    const url =
      `${this.convertFnM4aUrl}?companyId=${encodeURIComponent(meta.companyId)}`
      + `&phone=${encodeURIComponent(meta.phone)}`
      + `&messageType=${encodeURIComponent(meta.messageType ?? 'audio')}`;

    const resp = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/octet-stream'
      },
      body: webmBlob
    });

    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      throw new Error(`CONVERT_FN_FAILED_${resp.status}: ${text}`);
    }

    return await resp.json();
  }

  async convertWebMToMp3(webmBlob: Blob): Promise<Blob> {
    const audioBuffer = await this.blobToAudioBuffer(webmBlob);
    const mp3Blob = await this.audioBufferToMp3Blob(audioBuffer);
    return mp3Blob;
  }

  private async blobToAudioBuffer(blob: Blob): Promise<AudioBuffer> {
    const arrayBuffer = await this.blobToArrayBuffer(blob);
    const audioContext = new AudioContext();
    return await audioContext.decodeAudioData(arrayBuffer);
  }

  private async blobToArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
    return new Promise<ArrayBuffer>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = reject;
      reader.readAsArrayBuffer(blob);
    });
  }

  // lamejs (~170 kB) is only needed when the user records audio, so it is loaded on demand.
  private async audioBufferToMp3Blob(audioBuffer: AudioBuffer): Promise<Blob> {
    const module: any = await import('@breezystack/lamejs');
    const lamejs: any = module?.Mp3Encoder ? module : module?.default;
    const sampleRate = audioBuffer.sampleRate;
    const mp3encoder = new lamejs.Mp3Encoder(1, sampleRate, 192);
    const mp3Data = [];

    const sampleBlockSize = 1152;
    const samples = this.convertTo16BitPCM(audioBuffer);

    for (let i = 0; i < samples.length; i += sampleBlockSize) {
      const sampleChunk = samples.subarray(i, i + sampleBlockSize);
      const mp3buf = mp3encoder.encodeBuffer(sampleChunk);
      if (mp3buf.length > 0) {
        mp3Data.push(mp3buf);
      }
    }

    const mp3buf = mp3encoder.flush();
    if (mp3buf.length > 0) {
      mp3Data.push(mp3buf);
    }

    return new Blob(mp3Data, { type: 'audio/mp3' });
  }

  private convertTo16BitPCM(audioBuffer: AudioBuffer): Int16Array {
    const channelData = audioBuffer.getChannelData(0);
    const length = channelData.length;
    const result = new Int16Array(length);

    for (let i = 0; i < length; i++) {
      result[i] = this.floatTo16BitPCM(channelData[i]);
    }
    return result;
  }

  private floatTo16BitPCM(input: number): number {
    const s = Math.max(-1, Math.min(1, input));
    return s < 0 ? s * 0x8000 : s * 0x7FFF;
  }

  async convertWebMToWav(webmBlob: Blob): Promise<Blob> {
    const audioBuffer = await this.blobToAudioBuffer(webmBlob);
    const wav = this.audioBufferToWavBlob(audioBuffer);
    return wav;
  }

  private audioBufferToWavBlob(audioBuffer: AudioBuffer): Blob {
    const numChannels = audioBuffer.numberOfChannels;
    const sampleRate = audioBuffer.sampleRate;
    const interleaved = numChannels === 2
      ? this.interleave(audioBuffer.getChannelData(0), audioBuffer.getChannelData(1))
      : this.float32ToInt16(audioBuffer.getChannelData(0));

    const bytesPerSample = 2;
    const blockAlign = numChannels * bytesPerSample;
    const buffer = new ArrayBuffer(44 + interleaved.length * bytesPerSample);
    const view = new DataView(buffer);

    // RIFF header
    this.writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + interleaved.length * bytesPerSample, true);
    this.writeString(view, 8, 'WAVE');

    // fmt  subchunk
    this.writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);        // Subchunk1Size (16 for PCM)
    view.setUint16(20, 1, true);         // AudioFormat (1=PCM)
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * blockAlign, true); // ByteRate
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, 16, true);        // BitsPerSample

    // data subchunk
    this.writeString(view, 36, 'data');
    view.setUint32(40, interleaved.length * bytesPerSample, true);

    // PCM samples
    let offset = 44;
    for (let i = 0; i < interleaved.length; i++, offset += 2) {
      view.setInt16(offset, interleaved[i], true);
    }

    return new Blob([view], { type: 'audio/wav' });
  }

  private writeString(view: DataView, offset: number, str: string) {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
  }

  private float32ToInt16(src: Float32Array): Int16Array {
    const dst = new Int16Array(src.length);
    for (let i = 0; i < src.length; i++) {
      const s = Math.max(-1, Math.min(1, src[i]));
      dst[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }
    return dst;
  }

  private interleave(left: Float32Array, right: Float32Array): Int16Array {
    const len = Math.min(left.length, right.length);
    const interleaved = new Int16Array(len * 2);
    for (let i = 0, j = 0; i < len; i++) {
      interleaved[j++] = this.sampleToI16(left[i]);
      interleaved[j++] = this.sampleToI16(right[i]);
    }
    return interleaved;
  }

  private sampleToI16(s: number): number {
    s = Math.max(-1, Math.min(1, s));
    return s < 0 ? s * 0x8000 : s * 0x7FFF;
  }
}